import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../shared/_TBComponents/Button';
import Text from '../../../shared/_TBComponents/Text';
import Modal from '../../../shared/_TBComponents/Modal';
import Icon from '../../../shared/_TBComponents/Icon';
import Input from '../../../shared/_TBComponents/Input';
import Dropdown from '../../../shared/_TBComponents/Dropdown';
import ManageExercisesTable from './presentational/tables/ManageExercisesTable';
import { SearchWrapper, SearchIconWrapper } from './presentational/tables/NavigatorManageTable';
import ManageTagsTable from './presentational/tables/ManageTagsTable';
import ManageProgressionsTable from './presentational/tables/ManageProgressionsTable';
import ManageQuestionsTable from './presentational/tables/ManageQuestionsTable';
import MergeExerciseModal from './presentational/MergeExerciseModal';
import ExerciseTagModal from './presentational/modals/ExerciseTagModal';
import ProgressionModal from './presentational/modals/ProgressionModal';
import ModalHeading from './presentational/ModalHeading';
import ConfirmModal from './presentational/modals/ConfirmModal';
import TagFilterDropdown from './presentational/TagFilterDropdown';
import CreateNewExerciseForm from '../../calendar/components/presentational/CreateNewExerciseForm';
import VideoModal from '../../calendar/modals/VideoModal';
// Services the questionnaire form's second-layer modals (New Question, Add Asset) —
// this page has its own modal host, unlike the calendar page's CalendarModalHandler.
import { SecondLayerModal } from '../../calendar/CalendarModalHandler';
import {
  fetchAllExercisesForAccount, fetchImportedExercisesForAccount, fetchTagsForAccount,
  fetchImportedTagsForAccount,
  setActiveModal, deleteExercise, bulkDeleteExercises, moveExercisesToMaster,
  deleteTagsForAccount, fetchQuestionsForAccount, setQuestionCount, addAsset,
  clearNewlyCreatedQuestion, setActiveSecondLayerModal, bulkDeleteQuestionsForAccount,
} from '../../calendar/ducks/calendarActions';
import { showSuccessToast } from '../../../shared/utils/toastManager';
import generateUniqueId from '../../../shared/utils/generateUniqueId';
import { decodeVideoUrl } from '../../../shared/utils/decodeHtmlEntities';
import TYPE_OPTIONS from './presentational/exerciseTypeOptions';
import { EXERCISE_TYPE } from '../../../shared/constants';

// Full-screen "Exercises" page (4th Home tab, /dashboard/exercises). All mutations
// (edit/delete/bulk/move/merge) hit real Node endpoints.

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #F5F5F5;
  overflow: hidden;
`;

const TopSection = styled.div`
  background: #FFF;
  padding: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  /* Reserve the action button's height so tabs without one (Imported) don't
     make the whole page jump on tab switch. */
  min-height: 72px;
  box-sizing: border-box;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    min-height: 0;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const MobileBackLink = styled.button`
  display: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  align-items: center;
  gap: 6px;

  @media (max-width: 767px) {
    display: flex;
  }
`;

const DesktopTitle = styled.div`
  @media (max-width: 767px) {
    display: none;
  }
`;

const MobileSectionTitle = styled.div`
  display: none;
  font-size: 24px;
  font-weight: 700;
  color: #1A1A1A;

  @media (max-width: 767px) {
    display: block;
  }
`;

const BottomSection = styled.div`
  flex: 1;
  min-height: 0;
  background: #FFF;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// Tab strip mirrors Manage Athletes: divider under the tabs + spacing above the search.
const TabsRow = styled.div`
  display: flex;
  margin: 0 16px 16px 16px;
  box-sizing: border-box;
  flex-shrink: 0;
  border-bottom: 1px solid #D6D6D6;
`;

const TabButton = styled.button`
  padding-bottom: 8px;
  margin-right: 16px;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: ${(props) => (props.active ? 700 : 400)};
  color: #1A1A1A;
  border: none;
  border-bottom: 3px solid ${(props) => (props.active ? '#1787FB' : 'transparent')};
  background: none;
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;

  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const TypeTriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 0;
  border: none;
  background: none;
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #1A1A1A;
  white-space: nowrap;
  cursor: pointer;
`;

const TypeTriggerLabel = styled.span`
  font-weight: 700;
`;

const TypeTriggerBox = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  height: 40px;
  padding: 0 16px;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  background: #FFF;
  font-weight: 400;
`;

const TypeTrigger = ({ label }) => (
  <TypeTriggerButton type='button'>
    <TypeTriggerLabel>Type:</TypeTriggerLabel>
    <TypeTriggerBox>
      {label}
      <Icon icon='down-arrow' size={10} color='#616161' />
    </TypeTriggerBox>
  </TypeTriggerButton>
);

TypeTrigger.propTypes = {
  label: PropTypes.string.isRequired,
};

// Search-row composition: Type dropdown left of the unified filter field,
// Lives inside the table's searchControl slot.
const SearchControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  /* Narrow viewports: let the Tags button wrap under instead of overflowing. */
  flex-wrap: wrap;
`;

// The _TBComponents Modal lays its children out in a row, so wrap the header +
// form in a single column container to stack them vertically.
const CreateBody = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

// Scrollable name list for confirm modals covering more than one exercise.
const NameList = styled.ul`
  margin: 0;
  padding-left: 20px;
  max-height: 200px;
  overflow-y: auto;
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #444444;
`;

const ManageExercises = ({ backPath }) => {
  const history = useHistory();
  const location = useLocation();
  const cameFromManage = Boolean(location.state?.fromManage);
  const dispatch = useDispatch();
  const accountCode = useSelector((state) => state.auth.data.currentUser?.accountCode);
  const allExercises = useSelector((state) => state.calendar.data.allExercises);
  const tagsForAccount = useSelector((state) => state.calendar.data.tagsForAccount);
  const questionsForAccount = useSelector((state) => state.calendar.data.questionsForAccount);

  const [isLoading, setIsLoading] = useState(true);
  const [masterError, setMasterError] = useState(false);
  const [activeTab, setActiveTab] = useState('master');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isTypeMenuOpen, setIsTypeMenuOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // When set, the create modal runs in EDIT mode (PATCHes this exercise id).
  const [editExerciseId, setEditExerciseId] = useState(null);
  const [exerciseToDelete, setExerciseToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // The row that will be merged away (deleted); merge target is picked inside the modal.
  const [mergeSource, setMergeSource] = useState(null);
  const [clearSelectionToken, setClearSelectionToken] = useState(0);
  const [bulkDeleteIds, setBulkDeleteIds] = useState(null);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [moveIds, setMoveIds] = useState(null);
  const [isMoving, setIsMoving] = useState(false);
  // Set after a successful merge; scrolled-to once the post-merge refetch settles.
  const [pendingScrollId, setPendingScrollId] = useState(null);
  const tableRef = useRef(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  // Imported holding-pen rows (the `imported=true` fetch), held in local state.
  const [importedRaw, setImportedRaw] = useState(null);
  const [importedLoading, setImportedLoading] = useState(true);
  const [importedError, setImportedError] = useState(false);
  // Optimistic in-session hide for delete + merge; clears on reload.
  const [hiddenIds, setHiddenIds] = useState(() => new Set());
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagsError, setTagsError] = useState(false);
  const [importedTags, setImportedTags] = useState([]);
  // Free-text search — tokenized downstream over name + tag names, so typing
  // a tag name still narrows without the tag menu.
  const [filterInput, setFilterInput] = useState('');
  // Tag menu filter (AND) — narrows rows alongside the text search.
  const [tagFilterIds, setTagFilterIds] = useState([]);
  const typeOptions = useMemo(() => (
    activeTab === 'imported'
      ? [...TYPE_OPTIONS, { value: EXERCISE_TYPE.Progression, label: 'Progressions' }]
      : TYPE_OPTIONS
  ), [activeTab]);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  // Tag being edited; null while the modal is in create mode.
  const [editingTag, setEditingTag] = useState(null);
  const [tagToDelete, setTagToDelete] = useState(null);
  const [isDeletingTag, setIsDeletingTag] = useState(false);
  const [bulkDeleteTagIds, setBulkDeleteTagIds] = useState(null);
  const [isBulkDeletingTags, setIsBulkDeletingTags] = useState(false);
  const [tagsClearSelectionToken, setTagsClearSelectionToken] = useState(0);
  const [isProgressionModalOpen, setIsProgressionModalOpen] = useState(false);
  // Progression being edited; null while the modal is in create mode.
  const [editingProgression, setEditingProgression] = useState(null);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [questionsError, setQuestionsError] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [isDeletingQuestion, setIsDeletingQuestion] = useState(false);
  const [bulkDeleteQuestionIds, setBulkDeleteQuestionIds] = useState(null);
  const [isBulkDeletingQuestions, setIsBulkDeletingQuestions] = useState(false);
  const [questionsClearSelectionToken, setQuestionsClearSelectionToken] = useState(0);

  const loadMaster = () => {
    setIsLoading(true);
    setMasterError(false);
    dispatch(fetchAllExercisesForAccount(accountCode, {
      onSuccess: () => setIsLoading(false),
      onError: () => { setMasterError(true); setIsLoading(false); },
    }));
  };

  const loadImported = () => {
    setImportedLoading(true);
    setImportedError(false);
    dispatch(fetchImportedExercisesForAccount(accountCode, {
      onSuccess: (data) => { setImportedRaw(data || []); setImportedLoading(false); },
      onError: () => { setImportedError(true); setImportedLoading(false); },
    }));
  };

  const loadTags = () => {
    setTagsLoading(true);
    setTagsError(false);
    dispatch(fetchTagsForAccount(accountCode, {
      onSuccess: () => setTagsLoading(false),
      onError: () => { setTagsError(true); setTagsLoading(false); },
    }));
  };

  const loadImportedTags = () => {
    dispatch(fetchImportedTagsForAccount(accountCode, {
      onSuccess: (data) => setImportedTags(data || []),
      onError: () => {},
    }));
  };

  // Feeds both the Questions tab and the questionnaire create/edit dropdown
  // (calendar.data.questionsForAccount); Build prefetches it on page load too.
  const loadQuestions = () => {
    setQuestionsLoading(true);
    setQuestionsError(false);
    dispatch(fetchQuestionsForAccount(accountCode, {
      onSuccess: () => setQuestionsLoading(false),
      onError: () => { setQuestionsError(true); setQuestionsLoading(false); },
    }));
  };

  useEffect(() => {
    if (!accountCode) return;
    loadTags();
    loadImportedTags();
    loadMaster();
    loadImported();
    loadQuestions();
    // The load* functions are recreated every render (not memoized), so
    // listing them as deps would re-fire this on every render instead of
    // just once when accountCode becomes available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountCode, dispatch]);

  useEffect(() => {
    if (activeTab !== 'imported' && typeFilter === EXERCISE_TYPE.Progression) {
      setTypeFilter('all');
    }
  }, [activeTab, typeFilter]);

  // exercise.tags are tag IDs; merge imported tags so imported rows resolve names too.
  const tagNameById = useMemo(() => {
    const map = {};
    (tagsForAccount || []).forEach((t) => { map[t.id] = t.name; });
    importedTags.forEach((t) => { map[t.id] = t.name; });
    return map;
  }, [tagsForAccount, importedTags]);

  const resolveTags = (tags) => (tags || [])
    .map((t) => (typeof t === 'object' ? t : { id: t, name: tagNameById[t] }))
    .filter((t) => t.name);

  // The exercises endpoint returns a linked exercise's id but not its name, so
  // the "Linked Exercise" column resolves the name from the loaded list. Built
  // from the full set (not just masters) so links to any live exercise resolve;
  // links to deleted exercises (absent here) fall back to a "Linked max" label.
  const exerciseNameById = useMemo(() => {
    const map = new Map();
    (allExercises || []).forEach((e) => { if (e.name) map.set(e.id, e.name); });
    return map;
  }, [allExercises]);

  // Master = reusableItem===1, minus optimistic hides, tags resolved, linked
  // exercise name resolved. Progressions (type 'P') ride the same endpoint but
  // have their own tab, table shape, and edit form — keep them out of the
  // exercise tabs and the merge survivor pool.
  const masterExercises = useMemo(() => (allExercises || [])
    .filter((e) => e.reusableItem === 1 && e.type !== 'P')
    .filter((e) => !hiddenIds.has(e.id))
    .map((e) => ({
      ...e,
      tags: resolveTags(e.tags),
      linkedMaxExerciseName: e.linkedMaxExerciseName
        || (e.linkedMaxExerciseId ? exerciseNameById.get(e.linkedMaxExerciseId) || null : null),
    })), [allExercises, tagNameById, exerciseNameById, hiddenIds]);

  // Progressions tab: the type-'P' slice of the same master fetch (they carry
  // `levels`, the level count), minus optimistic hides.
  const progressions = useMemo(() => (allExercises || [])
    .filter((e) => e.reusableItem === 1 && e.type === 'P')
    .filter((e) => !hiddenIds.has(e.id)), [allExercises, hiddenIds]);

  // Imported-only set, minus optimistic hides, tags resolved.
  const importedExercises = useMemo(() => {
    if (!importedRaw) return [];
    return importedRaw
      .filter((e) => !hiddenIds.has(e.id))
      // Serializer field is `source` (the team an exercise was imported from);
      // the table surfaces it under the "Source Program" column.
      .map((e) => ({ ...e, tags: resolveTags(e.tags), sourceProgram: e.source }));
  }, [importedRaw, tagNameById, hiddenIds]);

  // Type + tag filters — name/tag text filtering is the flattened chip query,
  // applied downstream by the table's existing tokenized search. The tag menu
  // is AND semantics: each selected tag narrows the list, so an exercise must
  // carry every one of them.
  const visibleExercises = useMemo(() => {
    const source = activeTab === 'imported' ? importedExercises : masterExercises;
    return source
      .filter((e) => (typeFilter === 'all' ? true : e.type === typeFilter))
      .filter((e) => activeTab === 'imported' || tagFilterIds.every(
        (id) => (e.tags || []).some((t) => t.id === id),
      ));
  }, [masterExercises, importedExercises, activeTab, typeFilter, tagFilterIds]);

  const exerciseQuery = filterInput.trim();

  // The table clears search (empty string) when it needs to reveal a row hidden
  // by an active filter — e.g. scrolling to a merge survivor. Reset the field
  // and the tag menu, either of which could be hiding the target row.
  const handleSearchQueryChange = (next) => {
    if (!next) {
      setFilterInput('');
      setTagFilterIds([]);
    }
  };

  const handleNewExercise = () => {
    // Creating a question from the Questions tab appends a row to the shared
    // questionCount state (Build behavior); clear it so a fresh questionnaire
    // form doesn't open pre-seeded with that question.
    dispatch(setQuestionCount([{
      index: 0, id: `${generateUniqueId()}-null`, isNullValue: true, isVisible: true,
    }]));
    // Seed the shared form's type via modalData, then open the create modal.
    const type = (typeFilter === 'all' || typeFilter === EXERCISE_TYPE.Progression) ? 'L' : typeFilter;
    dispatch(setActiveModal('createNewComponentModal', { type }));
    setIsCreateOpen(true);
  };

  const closeCreate = () => {
    setIsCreateOpen(false);
    setEditExerciseId(null);
    dispatch(setActiveModal('', null));
    // The questionnaire inputs read questionCount / addAsset / newlyCreatedQuestion
    // from the calendar duck; Build's modal host resets them on close
    // (CalendarModalHandler), so this modal must too or edits leak into the
    // next create.
    dispatch(setQuestionCount([{
      index: 0, id: `${generateUniqueId()}-null`, isNullValue: true, isVisible: true,
    }]));
    dispatch(addAsset(null));
    dispatch(clearNewlyCreatedQuestion());
  };

  // Edit reuses the create form. The table row has tags resolved to {id,name},
  // but the form's prefill wants the raw record (tag ids + defaultOptions), so
  // re-fetch raw by id and synthesize a media entry so the video URL prefills.
  const handleEditExercise = (exercise) => {
    const raw = (allExercises || []).find((e) => e.id === exercise.id)
      || (importedRaw || []).find((e) => e.id === exercise.id)
      || exercise;
    let media = [];
    if (raw.media && raw.media.length) {
      media = raw.media;
    } else if (raw.videoUrl) {
      // Legacy video column with no media association — treat as URL-mode
      // ('VIDEO' means an uploaded file elsewhere in the media pipeline).
      media = [{ mediaUrl: raw.videoUrl, thumbnailUrl: '', type: 'EXTERNAL_URL' }];
    }
    dispatch(setActiveModal('createNewComponentModal', {
      type: raw.type,
      // The questionnaire inputs' prefill (fetch full exercise + seed question
      // rows) only runs when this flag is set; the list payload has no questions.
      editModal: true,
      itemInfoForEditModal: {
        ...raw,
        media,
        // Form validates link-% with min(0.01); an unlinked exercise stores 0,
        // which silently blocks save. 100 = create default (not sent when unlinked).
        linkedMaxPercentage: raw.linkedMaxPercentage || 100,
      },
    }));
    setEditExerciseId(raw.id);
    setIsCreateOpen(true);
  };

  // Refetch on save so the table reflects the PATCH (master is in redux; imported
  // is local state).
  const handleExerciseSaved = () => {
    dispatch(fetchAllExercisesForAccount(accountCode));
    if (activeTab === 'imported') {
      dispatch(fetchImportedExercisesForAccount(accountCode, {
        onSuccess: (data) => setImportedRaw(data || []),
      }));
    }
  };

  const handleMergeExercise = (exercise) => setMergeSource(exercise);

  // Merge target is always a master exercise, excluding the source itself and
  // any read-only (linked) rows, which can't be edited or merged into.
  const mergeCandidates = useMemo(() => {
    if (!mergeSource) return [];
    return masterExercises.filter((e) => e.id !== mergeSource.id && !e.isReadOnly);
  }, [mergeSource, masterExercises]);

  // Best-effort: scrolls once the post-merge refetch (and any tab switch) has
  // rendered; no-ops silently if the row got filtered out of view.
  useEffect(() => {
    if (pendingScrollId == null) return;
    tableRef.current?.scrollToExercise(pendingScrollId);
    setPendingScrollId(null);
  }, [pendingScrollId, activeTab, masterExercises]);

  const handlePlayVideo = (raw) => {
    if (!raw) return;
    setVideoPreviewUrl(decodeVideoUrl(raw));
  };

  const confirmDeleteExercise = () => {
    if (!exerciseToDelete || isDeleting) return;
    setIsDeleting(true);
    dispatch(deleteExercise(accountCode, exerciseToDelete.id, {
      onSuccess: () => {
        setHiddenIds((prev) => new Set(prev).add(exerciseToDelete.id));
        setExerciseToDelete(null);
        setIsDeleting(false);
      },
      onError: () => setIsDeleting(false),
    }));
  };

  // Hide a batch of ids optimistically (bulk delete + merge).
  const hideIds = (ids) => setHiddenIds((prev) => {
    const next = new Set(prev);
    ids.forEach((id) => next.add(id));
    return next;
  });

  const handleMerged = (removedIds, survivorName, survivorId) => {
    hideIds(removedIds);
    setClearSelectionToken((t) => t + 1);
    showSuccessToast(`Merged into "${survivorName}"`);
    // Merging from Imported means the survivor now lives in Master —
    // switch tabs first so the scroll-to lands on a rendered row.
    if (mergeSource?.isImported) setActiveTab('master');
    // Refetch so the survivor row reflects any field it took from the
    // source, then best-effort scroll to it once the table re-renders.
    dispatch(fetchAllExercisesForAccount(accountCode, {
      onSuccess: () => setPendingScrollId(survivorId),
    }));
  };

  // Resolve ids -> names for the bulk-delete confirm list, from whichever tab
  // the selection came from.
  const bulkDeleteExercisesList = useMemo(() => {
    if (!bulkDeleteIds) return [];
    let source = masterExercises;
    if (activeTab === 'imported') source = importedExercises;
    if (activeTab === 'progressions') source = progressions;
    return source.filter((e) => bulkDeleteIds.includes(e.id));
  }, [bulkDeleteIds, activeTab, importedExercises, masterExercises, progressions]);

  // The single-delete and bulk-delete confirms are shared with the Progressions
  // tab (same endpoints) — only the noun changes.
  const deleteNoun = exerciseToDelete?.type === 'P' ? 'progression' : 'exercise';
  const bulkNoun = activeTab === 'progressions' ? 'progression' : 'exercise';

  const confirmBulkDelete = () => {
    if (!bulkDeleteIds || bulkDeleteIds.length === 0 || isBulkDeleting) return;
    setIsBulkDeleting(true);
    dispatch(bulkDeleteExercises(accountCode, bulkDeleteIds, {
      onSuccess: ({ successful }) => {
        // Hide only the rows that actually deleted (partial-success aware).
        hideIds(successful && successful.length ? successful : bulkDeleteIds);
        setBulkDeleteIds(null);
        setClearSelectionToken((t) => t + 1);
        setIsBulkDeleting(false);
      },
      onError: () => setIsBulkDeleting(false),
    }));
  };

  // Move (Imported → Master) — both the row action and the bulk bar just open
  // the confirm modal; the actual dispatch is confirmMove below.
  const handleBulkMove = (ids) => {
    if (!ids || ids.length === 0) return;
    setMoveIds(ids);
  };

  const handleMoveExercise = (exercise) => handleBulkMove([exercise.id]);

  const moveExercisesList = useMemo(
    () => (moveIds ? importedExercises.filter((e) => moveIds.includes(e.id)) : []),
    [moveIds, importedExercises],
  );
  const moveDestination = useMemo(() => {
    const progressionCount = moveExercisesList
      .filter((e) => e.type === EXERCISE_TYPE.Progression).length;
    if (progressionCount === 0) return 'Our Exercises';
    if (progressionCount === moveExercisesList.length) return 'Progressions';
    return 'Our Exercises / Progressions';
  }, [moveExercisesList]);
  const moveLabel = useMemo(() => `Move to ${moveDestination}`, [moveDestination]);
  const progressionIds = moveExercisesList
    .filter((e) => e.type === EXERCISE_TYPE.Progression)
    .map((e) => e.id);

  // Refetch both lists instead of optimistically hiding: `hiddenIds` filters
  // both tabs, so a hide would also drop the row from Master, where it should
  // now appear.
  const confirmMove = () => {
    if (!moveIds || moveIds.length === 0 || isMoving) return;
    setIsMoving(true);
    dispatch(moveExercisesToMaster(accountCode, moveIds, {
      progressionIds,
      onSuccess: () => {
        setIsMoving(false);
        setMoveIds(null);
        dispatch(fetchAllExercisesForAccount(accountCode));
        dispatch(fetchImportedExercisesForAccount(accountCode, {
          onSuccess: (data) => setImportedRaw(data || []),
        }));
        setClearSelectionToken((t) => t + 1);
      },
      onError: () => setIsMoving(false),
    }));
  };

  const handleNewProgression = () => {
    setEditingProgression(null);
    setIsProgressionModalOpen(true);
  };

  const handleEditProgression = (progression) => {
    setEditingProgression(progression);
    setIsProgressionModalOpen(true);
  };

  const closeProgressionModal = () => {
    setIsProgressionModalOpen(false);
    setEditingProgression(null);
  };

  const handleProgressionSaved = () => {
    dispatch(fetchAllExercisesForAccount(accountCode));
  };

  // Reuses the calendar's New Question second-layer modal (rendered by the
  // SecondLayerModal mount below); it refetches questions itself on save.
  const handleNewQuestion = () => {
    dispatch(setActiveSecondLayerModal('newQuestionForm'));
  };

  const handleEditQuestion = (question) => {
    dispatch(setActiveSecondLayerModal('newQuestionForm', { editQuestion: question }));
  };

  const confirmDeleteQuestion = () => {
    if (!questionToDelete || isDeletingQuestion) return;
    setIsDeletingQuestion(true);
    // Single delete rides the bulk endpoint — there is no per-question DELETE.
    dispatch(bulkDeleteQuestionsForAccount(accountCode, [questionToDelete.id], {
      onSuccess: () => {
        setQuestionToDelete(null);
        setIsDeletingQuestion(false);
        dispatch(fetchQuestionsForAccount(accountCode));
      },
      onError: () => setIsDeletingQuestion(false),
    }));
  };

  const bulkDeleteQuestionsList = useMemo(
    () => (bulkDeleteQuestionIds
      ? (questionsForAccount || []).filter((q) => bulkDeleteQuestionIds.includes(q.id))
      : []),
    [bulkDeleteQuestionIds, questionsForAccount],
  );

  const confirmBulkDeleteQuestions = () => {
    if (!bulkDeleteQuestionIds || bulkDeleteQuestionIds.length === 0
      || isBulkDeletingQuestions) return;
    setIsBulkDeletingQuestions(true);
    dispatch(bulkDeleteQuestionsForAccount(accountCode, bulkDeleteQuestionIds, {
      onSuccess: () => {
        setBulkDeleteQuestionIds(null);
        setQuestionsClearSelectionToken((t) => t + 1);
        setIsBulkDeletingQuestions(false);
        dispatch(fetchQuestionsForAccount(accountCode));
      },
      onError: () => setIsBulkDeletingQuestions(false),
    }));
  };

  const handleNewTag = () => {
    setEditingTag(null);
    setIsTagModalOpen(true);
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setIsTagModalOpen(true);
  };

  const closeTagModal = () => {
    setIsTagModalOpen(false);
    setEditingTag(null);
  };

  // Silent refetches (no table spinners): exercise rows carry the tag chips and
  // the tag rows carry their exercises, so both sides need a refresh after the
  // modal saves (rename and/or association changes).
  const handleTagSaved = () => {
    dispatch(fetchTagsForAccount(accountCode));
    dispatch(fetchAllExercisesForAccount(accountCode));
  };

  const confirmDeleteTag = () => {
    if (!tagToDelete || isDeletingTag) return;
    setIsDeletingTag(true);
    dispatch(deleteTagsForAccount(accountCode, [tagToDelete.id], {
      onSuccess: () => {
        setTagToDelete(null);
        setIsDeletingTag(false);
      },
      onError: () => setIsDeletingTag(false),
    }));
  };

  const bulkDeleteTagsList = useMemo(
    () => (bulkDeleteTagIds
      ? (tagsForAccount || []).filter((t) => bulkDeleteTagIds.includes(t.id))
      : []),
    [bulkDeleteTagIds, tagsForAccount],
  );

  const confirmBulkDeleteTags = () => {
    if (!bulkDeleteTagIds || bulkDeleteTagIds.length === 0 || isBulkDeletingTags) return;
    setIsBulkDeletingTags(true);
    dispatch(deleteTagsForAccount(accountCode, bulkDeleteTagIds, {
      onSuccess: () => {
        setBulkDeleteTagIds(null);
        setTagsClearSelectionToken((t) => t + 1);
        setIsBulkDeletingTags(false);
      },
      onError: () => setIsBulkDeletingTags(false),
    }));
  };

  return (
    <Container>
      <TopSection>
        <TitleGroup>
          <MobileBackLink
            type='button'
            onClick={() => history.push(cameFromManage ? '/dashboard/manage' : backPath)}
          >
            <Icon icon='left-arrow' size={12} color='#77A7FF' />
            {cameFromManage ? 'Back to Manage' : 'Back to Home'}
          </MobileBackLink>
          <DesktopTitle>
            <Text styledTextProps={{ fontSize: '24px', fontWeight: 900, color: '#1A1A1A' }}>
              Exercises
            </Text>
          </DesktopTitle>
          <MobileSectionTitle>Exercises</MobileSectionTitle>
        </TitleGroup>
        {/* New exercises always go to master, so the button only shows there. */}
        {activeTab === 'master' && (
          <Button
            name='New Exercise'
            icon='plus'
            iconColor='#FFF'
            bgColor='#1A1A1A'
            borderColor='#1A1A1A'
            txtColor='#FFF'
            customBorderRadius='4px'
            fontSize='14px'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            onClick={handleNewExercise}
          />
        )}
        {activeTab === 'progressions' && (
          <Button
            name='New Progression'
            icon='plus'
            iconColor='#FFF'
            bgColor='#1A1A1A'
            borderColor='#1A1A1A'
            txtColor='#FFF'
            customBorderRadius='4px'
            fontSize='14px'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            onClick={handleNewProgression}
          />
        )}
        {activeTab === 'questions' && (
          <Button
            name='New Question'
            icon='plus'
            iconColor='#FFF'
            bgColor='#1A1A1A'
            borderColor='#1A1A1A'
            txtColor='#FFF'
            customBorderRadius='4px'
            fontSize='14px'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            onClick={handleNewQuestion}
          />
        )}
        {activeTab === 'tags' && (
          <Button
            name='New Tag'
            icon='plus'
            iconColor='#FFF'
            bgColor='#1A1A1A'
            borderColor='#1A1A1A'
            txtColor='#FFF'
            customBorderRadius='4px'
            fontSize='14px'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            onClick={handleNewTag}
          />
        )}
      </TopSection>

      <BottomSection>
        <TabsRow>
          {/* "Our Exercises" is the user-facing name for the master pool —
              internal ids/state keep the 'master' terminology. */}
          <TabButton type='button' active={activeTab === 'master'} onClick={() => setActiveTab('master')}>
            Our Exercises
          </TabButton>
          <TabButton type='button' active={activeTab === 'imported'} onClick={() => setActiveTab('imported')}>
            Imported
          </TabButton>
          <TabButton type='button' active={activeTab === 'progressions'} onClick={() => setActiveTab('progressions')}>
            Progressions
          </TabButton>
          <TabButton type='button' active={activeTab === 'questions'} onClick={() => setActiveTab('questions')}>
            Questions
          </TabButton>
          <TabButton type='button' active={activeTab === 'tags'} onClick={() => setActiveTab('tags')}>
            Tags
          </TabButton>
        </TabsRow>

        {activeTab === 'progressions' && (
          <ManageProgressionsTable
            progressions={progressions}
            isLoading={isLoading}
            hasError={masterError}
            onRetry={loadMaster}
            onEditProgression={handleEditProgression}
            onDeleteProgression={setExerciseToDelete}
            onBulkDelete={setBulkDeleteIds}
            clearSelectionToken={clearSelectionToken}
          />
        )}
        {activeTab === 'questions' && (
          <ManageQuestionsTable
            questions={questionsForAccount || []}
            isLoading={questionsLoading}
            hasError={questionsError}
            onRetry={loadQuestions}
            onEditQuestion={handleEditQuestion}
            onDeleteQuestion={setQuestionToDelete}
            onBulkDelete={setBulkDeleteQuestionIds}
            clearSelectionToken={questionsClearSelectionToken}
          />
        )}
        {activeTab === 'tags' && (
          <ManageTagsTable
            tags={tagsForAccount || []}
            isLoading={tagsLoading}
            hasError={tagsError}
            onRetry={loadTags}
            onEditTag={handleEditTag}
            onDeleteTag={setTagToDelete}
            onBulkDelete={setBulkDeleteTagIds}
            clearSelectionToken={tagsClearSelectionToken}
          />
        )}
        {(activeTab === 'master' || activeTab === 'imported') && (
          <ManageExercisesTable
            ref={tableRef}
            exercises={visibleExercises}
            isLoading={activeTab === 'imported' ? importedLoading : isLoading}
            hasError={activeTab === 'imported' ? importedError : masterError}
            onRetry={activeTab === 'imported' ? loadImported : loadMaster}
            activeTab={activeTab}
            emptyMessage={activeTab === 'imported'
              ? 'No imported exercises yet. Imported exercises appear here until you move them to your database.'
              : 'No exercises in your database yet.'}
            onEditExercise={handleEditExercise}
            onMoveExercise={handleMoveExercise}
            onDeleteExercise={setExerciseToDelete}
            onPlayVideo={handlePlayVideo}
            onBulkDelete={setBulkDeleteIds}
            onBulkMove={handleBulkMove}
            onMergeExercise={handleMergeExercise}
            clearSelectionToken={clearSelectionToken}
            searchControl={(
              <SearchControls>
                <Dropdown
                  isOpen={isTypeMenuOpen}
                  setIsOpen={setIsTypeMenuOpen}
                  CustomTriggerComponent={TypeTrigger}
                  customTriggerProps={{
                    label: (typeOptions.find((opt) => opt.value === typeFilter)
                      || typeOptions[0]).label,
                  }}
                  options={typeOptions.map((opt) => ({
                    text: opt.label,
                    icon: typeFilter === opt.value ? 'checkmark' : '',
                    function: () => setTypeFilter(opt.value),
                  }))}
                  iconColor='#1787FB'
                  menuWidth='220px'
                  menuBorder='1px solid #EEEEEE'
                  menuBorderRadius='6px'
                  menuPaddingTop='4px'
                  menuPaddingBottom='4px'
                />
                <SearchWrapper>
                  <SearchIconWrapper>
                    <Icon icon='magnifying-glass' size={16} color='#929292' />
                  </SearchIconWrapper>
                  <Input
                    name='manage-exercises-search'
                    inputOnly
                    placeholder='Search exercises and tags'
                    value={filterInput}
                    onChange={(e) => setFilterInput(e.target.value)}
                    styledPropsInput={{
                      width: '100%',
                      height: '40px',
                      fontSize: '14px',
                      paddingLeft: '36px',
                    }}
                    stylePropsWrapper={{ border: '1px solid #D6D6D6', borderRadius: '4px' }}
                  />
                </SearchWrapper>
                {activeTab !== 'imported' && (
                  <TagFilterDropdown
                    tags={tagsForAccount || []}
                    selectedIds={tagFilterIds}
                    onChange={setTagFilterIds}
                  />
                )}
              </SearchControls>
            )}
            searchQuery={exerciseQuery}
            onSearchQueryChange={handleSearchQueryChange}
          />
        )}
      </BottomSection>

      <Modal
        isOpen={isCreateOpen}
        onRequestClose={closeCreate}
        hideButton
        hideTitle
        // Sized to the form's two-column content (~720px); wider just left-aligns
        // it and leaves a gap.
        customMaxWidth='780px'
        customMinHeight='0'
        customPadding='24px'
        customBorderRadius='8px'
      >
        {isCreateOpen && (
          <CreateBody>
            <ModalHeading
              title={editExerciseId ? 'Edit Exercise' : 'Create New Exercise'}
              onClose={closeCreate}
            />
            <CreateNewExerciseForm
              closeModal={closeCreate}
              editExerciseId={editExerciseId}
              isManageExercisesContext
              unifiedVideoFlow
              // Create: no-op override (just suppresses the form's calendar
              // side-effects). Edit: refetch so the table reflects the PATCH.
              onSuccessOverride={editExerciseId ? handleExerciseSaved : () => {}}
              skipSimilarityCheck
            />
          </CreateBody>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!exerciseToDelete}
        onClose={() => setExerciseToDelete(null)}
        onConfirm={confirmDeleteExercise}
        title={deleteNoun === 'progression' ? 'Delete Progression' : 'Delete Exercise'}
        message={`Are you sure you want to delete "${exerciseToDelete?.name || `this ${deleteNoun}`}"? This can't be undone.`}
        isLoading={isDeleting}
      />

      <ConfirmModal
        isOpen={!!bulkDeleteIds}
        onClose={() => setBulkDeleteIds(null)}
        onConfirm={confirmBulkDelete}
        title={bulkNoun === 'progression' ? 'Delete Progressions' : 'Delete Exercises'}
        message={`Are you sure you want to delete the following ${bulkDeleteIds?.length || 0} ${
          (bulkDeleteIds?.length || 0) === 1 ? bulkNoun : `${bulkNoun}s`}? This can't be undone.`}
        isLoading={isBulkDeleting}
      >
        <NameList>
          {bulkDeleteExercisesList.map((e) => (
            <li key={e.id}>{e.name || 'Untitled exercise'}</li>
          ))}
        </NameList>
      </ConfirmModal>

      <ConfirmModal
        isOpen={!!moveIds}
        onClose={() => setMoveIds(null)}
        onConfirm={confirmMove}
        title={moveLabel}
        message={`Are you sure you want to move the following ${moveIds?.length || 0} ${
          (moveIds?.length || 0) === 1 ? 'exercise' : 'exercises'} to ${moveDestination}?${moveDestination === 'Our Exercises / Progressions' ? ' Progressions will appear under the Progressions tab.' : ''}`}
        confirmLabel={moveLabel}
        confirmIcon='add-program'
        destructive={false}
        isLoading={isMoving}
      >
        <NameList>
          {moveExercisesList.map((e) => (
            <li key={e.id}>{e.name || 'Untitled exercise'}</li>
          ))}
        </NameList>
      </ConfirmModal>

      <MergeExerciseModal
        // Remount per source: the wizard holds step/survivor state, and a
        // stale survivor from a previous merge can be absent from the new
        // candidates (e.g. merging into last round's winner) — rendering
        // step 3 with no survivor crashes before the reset effect runs.
        key={mergeSource?.id ?? 'closed'}
        isOpen={!!mergeSource}
        sourceExercise={mergeSource}
        candidates={mergeCandidates}
        accountCode={accountCode}
        onClose={() => setMergeSource(null)}
        onPlayVideo={handlePlayVideo}
        onMerged={handleMerged}
      />

      <Modal
        // Remount per URL: overlays share a z-index, so stacking follows portal
        // DOM order — remounting on open appends this portal last, keeping the
        // preview above the (also remount-keyed) merge modal.
        key={videoPreviewUrl ?? 'video-closed'}
        isOpen={!!videoPreviewUrl}
        onRequestClose={() => setVideoPreviewUrl(null)}
        hideTitle
        customMaxWidth='820px'
        customMinHeight='0'
        customPadding='24px'
        customBorderRadius='8px'
        hideButton
      >
        {videoPreviewUrl && (
          <CreateBody>
            <ModalHeading title='Video preview' onClose={() => setVideoPreviewUrl(null)} />
            <VideoModal url={videoPreviewUrl} />
          </CreateBody>
        )}
      </Modal>

      <ExerciseTagModal
        isOpen={isTagModalOpen}
        onClose={closeTagModal}
        accountCode={accountCode}
        tag={editingTag}
        onSaved={handleTagSaved}
      />

      <ProgressionModal
        isOpen={isProgressionModalOpen}
        onClose={closeProgressionModal}
        accountCode={accountCode}
        progression={editingProgression}
        onSaved={handleProgressionSaved}
      />

      <ConfirmModal
        isOpen={!!questionToDelete}
        onClose={() => setQuestionToDelete(null)}
        onConfirm={confirmDeleteQuestion}
        title='Delete Question'
        message={`Are you sure you want to delete "${questionToDelete?.title || 'this question'}"? It will be removed from all questionnaires. This can't be undone.`}
        isLoading={isDeletingQuestion}
      />

      <ConfirmModal
        isOpen={!!bulkDeleteQuestionIds}
        onClose={() => setBulkDeleteQuestionIds(null)}
        onConfirm={confirmBulkDeleteQuestions}
        title='Delete Questions'
        message={`Are you sure you want to delete the following ${bulkDeleteQuestionIds?.length || 0} ${
          (bulkDeleteQuestionIds?.length || 0) === 1 ? 'question' : 'questions'}? They will be removed from all questionnaires. This can't be undone.`}
        isLoading={isBulkDeletingQuestions}
      >
        <NameList>
          {bulkDeleteQuestionsList.map((q) => (
            <li key={q.id}>{q.title || 'Untitled question'}</li>
          ))}
        </NameList>
      </ConfirmModal>

      <ConfirmModal
        isOpen={!!tagToDelete}
        onClose={() => setTagToDelete(null)}
        onConfirm={confirmDeleteTag}
        title='Delete Tag'
        message={`Are you sure you want to delete "${tagToDelete?.name || 'this tag'}"? It will be removed from all exercises. This can't be undone.`}
        isLoading={isDeletingTag}
      />

      <ConfirmModal
        isOpen={!!bulkDeleteTagIds}
        onClose={() => setBulkDeleteTagIds(null)}
        onConfirm={confirmBulkDeleteTags}
        title='Delete Tags'
        message={`Are you sure you want to delete the following ${bulkDeleteTagIds?.length || 0} ${
          (bulkDeleteTagIds?.length || 0) === 1 ? 'tag' : 'tags'}? They will be removed from all exercises. This can't be undone.`}
        isLoading={isBulkDeletingTags}
      >
        <NameList>
          {bulkDeleteTagsList.map((t) => (
            <li key={t.id}>{t.name || 'Untitled tag'}</li>
          ))}
        </NameList>
      </ConfirmModal>

      <SecondLayerModal />
    </Container>
  );
};

ManageExercises.propTypes = {
  backPath: PropTypes.string,
};

ManageExercises.defaultProps = {
  backPath: '/dashboard',
};

export default ManageExercises;
